import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Attach a rejection-detail supplier's email to its claims' EDI 849s (SD-12).
 *
 * Runs after `identify_849_email`, gated on its `attachRejectionDetail` output:
 * such an email is never an 849 and never reaches `process_sd`. It joins each EDI
 * 849 its subject's batches name as context and adds only the supplier's reasons
 * as Rejection detail. When a batch has no EDI 849 (or several), nothing is
 * attached and the email waits in the review queue with the reason.
 */
export const attach849RejectionDetailStep = defineStepConfig({
  type: "attach_849_rejection_detail",
  name: "Attach 849 Rejection Detail",
  description:
    "For a supplier whose emails only explain its EDI 849 rejections, attach the email " +
    "to the EDI 849s its subject's batches name and add the supplier's reasons as " +
    "rejection detail. Never decides a line or changes a status. Skips unless " +
    "Identify 849 Email routed the email here.",
  category: "Transformation",
  libraryModule: "ShipAndDebit",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The supplier's rejection-detail email",
    ),
    customerName: z
      .string()
      .nullish()
      .describe("Supplier/customer name returned by Identify SD Customer"),
    attachRejectionDetail: z
      .boolean()
      .nullish()
      .describe(
        "From Identify 849 Email: whether this is a rejection-detail email. If not true, the step does nothing.",
      ),
  }),

  outputSchema: z.object({
    attached: z
      .boolean()
      .describe("Whether the email was attached to its EDI 849s"),
    status: z.enum(["applied", "not_confident", "skipped"]),
    reason: z
      .string()
      .nullable()
      .describe("Why nothing was attached (null when it was)"),
    sd849ResponseIds: z
      .array(z.string())
      .describe("The EDI 849 responses the email was attached to"),
    detailLineCount: z
      .number()
      .int()
      .describe("Lines that received the supplier's rejection detail"),
    unattachedRowCount: z
      .number()
      .int()
      .describe("Workbook rows placed on no line"),
  }),

  configSchema: z.object({}),
  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
